import { create } from "zustand";
import { widgetApi } from "../lib/api-client";
import { ChatMessage, WidgetConfig, WidgetProduct } from "../lib/types";

type LoadStatus = "idle" | "loading" | "loaded" | "error";

type WidgetState = {
  isOpen: boolean;
  activeTab: "chat" | "shop";

  config: WidgetConfig | null;
  configStatus: LoadStatus;
  configError: string | null;

  messages: ChatMessage[];
  messagesStatus: LoadStatus;
  messagesError: string | null;
  isSending: boolean;

  products: WidgetProduct[];
  productsStatus: LoadStatus;
  productsError: string | null;

  toggleOpen: () => void;
  setActiveTab: (tab: "chat" | "shop") => void;

  loadConfig: (publicKey: string) => Promise<void>;
  loadMessages: (publicKey: string, externalCustomerId: string) => Promise<void>;
  sendMessage: (
    publicKey: string,
    externalCustomerId: string,
    content: string
  ) => Promise<void>;
  loadProducts: (publicKey: string) => Promise<void>;
};

export const useWidgetStore = create<WidgetState>((set, get) => ({
  isOpen: false,
  activeTab: "chat",

  config: null,
  configStatus: "idle",
  configError: null,

  messages: [],
  messagesStatus: "idle",
  messagesError: null,
  isSending: false,

  products: [],
  productsStatus: "idle",
  productsError: null,

  toggleOpen: () => set((state) => ({ isOpen: !state.isOpen })),
  setActiveTab: (tab) => set({ activeTab: tab }),

  loadConfig: async (publicKey) => {
    set({ configStatus: "loading", configError: null });
    try {
      const config = await widgetApi.getConfig(publicKey);
      set({ config, configStatus: "loaded" });
    } catch (error) {
      set({
        configStatus: "error",
        configError: error instanceof Error ? error.message : "Failed to load",
      });
    }
  },

  loadMessages: async (publicKey, externalCustomerId) => {
    set({ messagesStatus: "loading", messagesError: null });
    try {
      const { messages } = await widgetApi.getMessages(
        publicKey,
        externalCustomerId
      );
      set({ messages, messagesStatus: "loaded" });
    } catch (error) {
      set({
        messagesStatus: "error",
        messagesError: error instanceof Error ? error.message : "Failed to load",
      });
    }
  },

  sendMessage: async (publicKey, externalCustomerId, content) => {
    if (!content.trim()) return;

    set({ isSending: true });
    try {
      const { message } = await widgetApi.sendMessage(publicKey, {
        externalCustomerId,
        content,
      });
      set({ messages: [...get().messages, message] });
    } finally {
      set({ isSending: false });
    }
  },

  loadProducts: async (publicKey) => {
    set({ productsStatus: "loading", productsError: null });
    try {
      const { products } = await widgetApi.getProducts(publicKey);
      set({ products, productsStatus: "loaded" });
    } catch (error) {
      set({
        productsStatus: "error",
        productsError: error instanceof Error ? error.message : "Failed to load",
      });
    }
  },
}));
